import React, { useCallback } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeGrid as Grid } from 'react-window';

interface VirtualizedGridProps<T> {
  items: T[];
  renderItem: (props: { index: number; style: React.CSSProperties; data: T[] }) => React.ReactElement | null;
  itemsPerRow?: number;
  itemHeight?: number;
  gap?: number;
  className?: string;
}

export function VirtualizedGrid<T>({
  items,
  renderItem,
  itemsPerRow = 3,
  itemHeight = 280,
  gap = 16,
  className,
}: VirtualizedGridProps<T>) {
  const rowCount = Math.ceil(items.length / itemsPerRow);

  const GridRow = useCallback(
    ({ rowIndex, style }: { rowIndex: number; style: React.CSSProperties }) => {
      const startIndex = rowIndex * itemsPerRow;

      return (
        <div style={style} className="flex gap-4">
          {Array.from({ length: itemsPerRow }, (_, colIndex) => {
            const itemIndex = startIndex + colIndex;
            const item = items[itemIndex];

            if (!item) {
              return <div key={colIndex} className="flex-1" />;
            }

            const renderedItem = renderItem({
              index: itemIndex,
              style: { width: '100%', height: itemHeight - gap },
              data: items
            });

            return (
              <div key={colIndex} className="flex-1">
                {renderedItem}
              </div>
            );
          })}
        </div>
      );
    },
    [items, itemsPerRow, itemHeight, gap, renderItem]
  );

  return (
    <div className={`h-full w-full ${className || ''}`}>
      <AutoSizer>
        {({ height, width }) => (
          <Grid
            className="overflow-auto"
            height={height}
            width={width}
            columnCount={1}
            columnWidth={width}
            rowCount={rowCount}
            rowHeight={itemHeight}
          >
            {GridRow}
          </Grid>
        )}
      </AutoSizer>
    </div>
  );
} 